import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/Common';

type AuthMode = 'signin' | 'signup';

const MusicNote = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
  </svg>
);

export function LoginPage() {
  const { login, isLoading } = useAuth();
  const [mode, setMode] = useState<AuthMode>('signin');

  const handleAuth = async () => {
    try {
      await login();
    } catch (error) {
      console.error('Authentication failed:', error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-dark-bg relative overflow-hidden">
      {/* Animated background gradients */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-radial from-primary-600/10 via-transparent to-transparent animate-pulse-slow" />
        <div className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-radial from-primary-800/8 via-transparent to-transparent animate-pulse-slower" />
      </div>

      {/* Floating music notes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <MusicNote className="absolute top-[15%] left-[10%] w-6 h-6 text-primary-600/20 animate-float-1" />
        <MusicNote className="absolute top-[25%] right-[15%] w-8 h-8 text-primary-500/15 animate-float-2" />
        <MusicNote className="absolute bottom-[20%] left-[20%] w-5 h-5 text-primary-700/20 animate-float-3" />
        <MusicNote className="absolute bottom-[30%] right-[10%] w-7 h-7 text-primary-600/15 animate-float-1" />
        <MusicNote className="absolute top-[60%] left-[5%] w-4 h-4 text-primary-500/10 animate-float-2" />
      </div>

      {/* Main content */}
      <div className="relative z-10 w-full max-w-md mx-4 animate-fadeIn">
        {/* Logo and branding */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-linear-to-br from-primary-500 to-primary-700 shadow-lg shadow-primary-600/30 mb-6">
            <MusicNote className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-dark-text-primary tracking-tight mb-2">
            BookMyBand
          </h1>
          <p className="text-dark-text-secondary text-base">
            The booking app for busy musicians
          </p>
        </div>

        {/* Glass card */}
        <div className="bg-dark-surface/80 backdrop-blur-xl rounded-2xl border border-dark-border/50 shadow-2xl shadow-black/50 p-8">
          {/* Tab switcher */}
          <div className="flex mb-8 bg-dark-bg/50 rounded-xl p-1.5">
            <button
              onClick={() => setMode('signin')}
              className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition-all duration-300 ${
                mode === 'signin'
                  ? 'bg-primary-600 text-white shadow-lg shadow-primary-600/30'
                  : 'text-dark-text-secondary hover:text-dark-text-primary'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => setMode('signup')}
              className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition-all duration-300 ${
                mode === 'signup'
                  ? 'bg-primary-600 text-white shadow-lg shadow-primary-600/30'
                  : 'text-dark-text-secondary hover:text-dark-text-primary'
              }`}
            >
              Sign Up
            </button>
          </div>

          {mode === 'signin' ? (
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-xl font-semibold text-dark-text-primary mb-2">
                  Welcome back
                </h2>
                <p className="text-dark-text-secondary text-sm">
                  Sign in to continue managing your bookings
                </p>
              </div>
              <Button
                variant="google"
                size="lg"
                onClick={handleAuth}
                isLoading={isLoading}
                loadingText="Connecting..."
                className="w-full h-12 text-base rounded-xl"
              >
                Sign in with Google
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-xl font-semibold text-dark-text-primary mb-2">
                  Create your account
                </h2>
                <p className="text-dark-text-secondary text-sm">
                  Start managing venues and bookings today
                </p>
              </div>
              <Button
                variant="google"
                size="lg"
                onClick={handleAuth}
                isLoading={isLoading}
                loadingText="Connecting..."
                className="w-full h-12 text-base rounded-xl"
              >
                Sign up with Google
              </Button>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-xs text-dark-text-muted">
          By {mode === 'signin' ? 'signing in' : 'creating an account'}, you
          agree to our{' '}
          <span className="text-dark-text-secondary hover:text-primary-400 cursor-pointer transition-colors">
            terms of service
          </span>{' '}
          and{' '}
          <span className="text-dark-text-secondary hover:text-primary-400 cursor-pointer transition-colors">
            privacy policy
          </span>
        </p>
      </div>
    </div>
  );
}
